import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { twMerge } from 'tailwind-merge';

import { trackEvents } from '@boss/constants/shared';
import { RichText } from '@boss/rich-text';
import { InspirationFields, InspirationImageFields, SimpleColor } from '@boss/types/b2b-b2c';
import { ColoraButton as Button, InspirationImage, Section, icons } from '@boss/ui';
import { getEntries, getInspirationTranslatedSlug } from '@boss/utils';

import { useProductsById } from '../client-queries';
import { CarouselProductsWrapper, ColorGridWrapper, CtaCardsSection, ProductCardWrapper } from '../components';
import { useProductMap } from '../hooks';
import { inspirationGridVariant, isB2b } from '../utils';

type Props = {
  inspiration: InspirationFields;
  locale: string;
  relatedImages: InspirationImageFields[];
  usedColors: SimpleColor[];
};

type ButtonProps = {
  label: string;
  filterKey: string;
  filterValue: string;
  className: string;
  locale: string;
};

const FilterButtonStyles = cva('mx-2 my-2', {
  variants: {
    variant: {
      primary: '',
      secondary: 'rounded-none',
    },
  },
});

const FilterButton = ({ label, filterKey, filterValue, className, locale }: ButtonProps) => {
  const translatedSlug = getInspirationTranslatedSlug(locale);
  // Use the raw Algolia facet key (filterKey) in the URL, not the translated label.
  // The overview page passes this directly to InspirationGrid which builds: filterKey:"filterValue"
  const query = new URLSearchParams({ filt: filterKey, val: filterValue }).toString();

  return (
    <Button
      className={twMerge(FilterButtonStyles({ variant: inspirationGridVariant }), className)}
      href={`${translatedSlug}?${query}`}
      label={label}
      testId={'button-with-href-to-' + translatedSlug}
      type="primary"
    />
  );
};

const InspirationDetailPage = ({ relatedImages, inspiration, locale, usedColors }: Props) => {
  const productIds = inspiration?.usedProducts?.map(product => product.productID) || [];
  const { mapProductComponent } = useProductMap();
  const { data: relatedProducts } = useProductsById(productIds, true, true);
  const { t } = useTranslation(['common', 'product', 'inspiration']);
  const straightCornerPosition = 3;

  const { title, description, image, filters } = inspiration;
  const hasFilters = filters && Object.values(filters).some(arr => arr?.length);

  return (
    <div className="w-full">
      <div className="lg:gap-30 grid grid-cols-1 gap-10 md:grid-cols-5">
        <InspirationImage
          asset={image.image}
          className="rounded-35 mb-0 shadow-lg md:col-span-3"
          imageClassName="h-full"
          order={3}
          showPinterestButton={!isB2b}
          sizes="(max-width: 400px) 100vw, (min-width: 768px) 40vw, 100vw"
          width={1000}
        />
        <div className="flex flex-col md:col-span-2 md:justify-center">
          <h1 className="h3 mb-5">
            <RichText
              content={title}
              mapperOptions={{ locale }}
              renderOptions={{
                noParagraph: true,
                noWrapper: true,
              }}
            />
          </h1>
          {description && <RichText content={description} mapperOptions={{ locale }} />}
        </div>
      </div>

      {!!usedColors.length && (
        <Section
          content={
            <div className="flex flex-col gap-5">
              <ColorGridWrapper
                className="md:w-full md:max-w-full"
                colors={usedColors}
                gridClassName="md:gap-5 lg:gap-10 md:grid-cols-6 lg:grid-cols-8"
                onClickAction="navigate"
                onFavorite={() => '/'} // TODO: Implement onFavorite post wishlist
                title={t('usedColors.title', { ns: 'inspiration' })}
                titleClassName="h4"
                translations={t('colorPickerModal', { ns: 'product', returnObjects: true })}
                variant="secondary"
              ></ColorGridWrapper>
              {!isB2b && (
                <div className="relative flex flex-row items-center gap-4 md:mt-0 md:pl-0">
                  <FontAwesomeIcon
                    className="text-red-dark rotate-[70deg] text-[5rem]"
                    icon={icons.pointingArrowRight}
                  />
                  <p className="caption pt-10 italic md:mt-0">
                    {t('usedColors.disclaimer.1', { ns: 'inspiration' })}
                    <br />
                    {t('usedColors.disclaimer.2', { ns: 'inspiration' })}
                  </p>
                </div>
              )}
            </div>
          }
        />
      )}

      {!!relatedProducts?.length && (
        <Section
          content={
            <CarouselProductsWrapper
              products={relatedProducts}
              title={t('recommendedProducts.title', { ns: 'product' }) as string}
            >
              {relatedProducts.map((product, index) => {
                if (!product) {
                  return null;
                }

                const _trackInfo = {
                  customEvent: trackEvents.SELECT_ITEM,
                  index,
                  item_list_name: 'related products',
                  item_id: product.id,
                  item_name: product.name,
                };

                return <ProductCardWrapper {...mapProductComponent(product)} key={product.id} trackInfo={_trackInfo} />;
              })}
            </CarouselProductsWrapper>
          }
        />
      )}

      {hasFilters && (
        <div className="mb-10 flex flex-wrap items-baseline">
          <p>{t('inspirationMore', { ns: 'inspiration' })}</p>
          {getEntries(filters).flatMap(([filter, valueArr]) =>
            valueArr?.map(option => (
              <FilterButton
                className="hover:bg-brown-dark bg-beige-light text-brown-dark mx-2 my-2 hover:text-white"
                filterKey={filter}
                filterValue={option}
                key={option}
                label={option}
                locale={locale}
              />
            )),
          )}
        </div>
      )}

      {!!inspiration.generalContent?.ctaSection && (
        <CtaCardsSection className="pt-0 md:pt-0" ctaSection={inspiration.generalContent.ctaSection} />
      )}

      {!!relatedImages.length && (
        <>
          <h3 className="mb-9">{t('inspirationStyles', { ns: 'inspiration' })}</h3>
          <div className="width-full mb-25 md:mb-50 flex flex-wrap gap-6 md:flex-nowrap">
            {relatedImages.map(({ slug, image, id }, i) => (
              <InspirationImage
                asset={image.image}
                className="rounded-35 block w-full shadow-lg"
                imageClassName="h-85"
                key={id}
                order={i + straightCornerPosition}
                showPinterestButton={!isB2b}
                sizes="(max-width: 400px) 100vw, (min-width: 768px) 40vw, 100vw"
                slug={slug}
                width={1000}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default InspirationDetailPage;
